import type { TrainingTextInputKind } from '@hcc/types';

/**
 * Fill-in-the-blank answer: a text input, or a textarea when the question is
 * authored multiline (the "supporting comments" box at the end of an
 * assessment), or the browser's native date / time picker when the row asks
 * for one. Controlled; knows nothing about which answers are accepted
 * (matching is server-side, case- and whitespace-insensitive).
 */

/** The HTML input type for each picker kind. */
const NATIVE_TYPE: Record<Exclude<TrainingTextInputKind, 'text'>, string> = {
  date: 'date',
  time: 'time',
  datetime: 'datetime-local',
};

export function ShortTextAnswer({
  value,
  onChange,
  disabled,
  ariaLabelledBy,
  multiline,
  placeholder,
  kind = 'text',
}: {
  value: string | undefined;
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
  multiline?: boolean;
  placeholder?: string;
  /** Defaults to a typed box; the others render a native picker. */
  kind?: TrainingTextInputKind;
}) {
  const shared =
    'block w-full rounded-token-sm border border-line-strong/20 bg-surface px-4 py-3 text-sm text-fg outline-none focus:border-accent disabled:opacity-60';
  if (kind !== 'text') {
    return (
      <input
        type={NATIVE_TYPE[kind]}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-labelledby={ariaLabelledBy}
        className={`${shared} max-w-xs`}
      />
    );
  }
  if (multiline) {
    return (
      <textarea
        rows={8}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-labelledby={ariaLabelledBy}
        placeholder={placeholder ?? 'Type your comments'}
        className={`${shared} resize-y`}
      />
    );
  }
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-labelledby={ariaLabelledBy}
      placeholder={placeholder ?? 'Type your answer'}
      autoComplete="off"
      className={shared}
    />
  );
}
